import { Dispatch } from "react";
import * as actionTypes from "../actionTypes/Index";
import {
  IActionType,
  ICreateUpdateRiderManagementUserNormalizedReq,
  IGetQueryParams,
  IJobTypes,
  IPlanCreation,
  IUserDetailsResponseObject,
} from "interfaces/Index";
import {
  ICreateRiderRequestPayload,
  ICreateUpdateRiderRequestPayload,
  IRiderManagementNormalized,
} from "interfaces/riderManagement.interface";
import {
  createRiderApiCall,
  fetchRiderListApiCall,
  getLocusCsvApiCall,
  getPlanOutputCsvApiCall,
  getRiderJobTypeApiCall,
  getRiderUserDetailsApiCall,
  updateRiderApiCall,
} from "../sources/RiderManagementSource";
import userSource, { UserAddUpdateDto } from "../sources/UserSource";
import { appendSubTypeWithJobTypeForRiderTitle } from "normalizer/RiderManagementNormalizer";

export const createRiderManagementUserInit = () => ({
  type: actionTypes.CREATE_RIDER_MANAGEMENT_USERS_INIT,
});

export const createRiderManagementUserSuccess = () => ({
  type: actionTypes.CREATE_RIDER_MANAGEMENT_USERS_SUCCESS,
});

export const createRiderManagementUserFail = (
  error: string | object | null
) => ({
  type: actionTypes.CREATE_RIDER_MANAGEMENT_USERS_FAIL,
  error,
});

//
export const createRiderInit = () => ({
  type: actionTypes.CREATE_RIDER_INIT,
});

export const createRiderSuccess = () => ({
  type: actionTypes.CREATE_RIDER_SUCCESS,
});

export const createRiderFail = (error: string | object | null) => ({
  type: actionTypes.CREATE_RIDER_FAIL,
  error,
});

export const getRiderManagementListInit = () => ({
  type: actionTypes.GET_RIDER_MANAGEMENT_LIST_INIT,
});

export const getRiderManagementListSuccess = (
  payload: IRiderManagementNormalized | null
) => ({
  type: actionTypes.GET_RIDER_MANAGEMENT_LIST_SUCCESS,
  payload,
});

export const getRiderManagementListFail = (error: string | object | null) => ({
  type: actionTypes.GET_RIDER_MANAGEMENT_LIST_FAIL,
  error,
});

export const getRiderUserDetailsInit = () => ({
  type: actionTypes.GET_RIDER_USER_DETAIL_INIT,
});

export const getRiderUserDetailsSuccess = (
  payload: IUserDetailsResponseObject
) => ({
  type: actionTypes.GET_RIDER_USER_DETAIL_SUCCESS,
  payload,
});

export const getRiderUserDetailsFail = (error: string | object | null) => ({
  type: actionTypes.GET_RIDER_USER_DETAIL_FAIL,
  error,
});

export const updateRiderManagementUserInit = () => ({
  type: actionTypes.UPDATE_RIDER_MANAGEMENT_USER_INIT,
});

export const updateRiderManagementUserSuccess = () => ({
  type: actionTypes.UPDATE_RIDER_MANAGEMENT_USER_SUCCESS,
});

export const updateRiderManagementUserFail = (
  error: string | object | null
) => ({
  type: actionTypes.UPDATE_RIDER_MANAGEMENT_USER_FAIL,
  error,
});

export const downloadLocusCsvInit = () => ({
  type: actionTypes.DOWNLOAD_LOCUS_PLAN_CSV_INIT,
});

export const downloadLocusCsvSuccess = (payload: FormData) => ({
  type: actionTypes.DOWNLOAD_LOCUS_PLAN_CSV_SUCCESS,
  payload,
});

export const downloadLocusCsvFail = (error: string | object | null) => ({
  type: actionTypes.DOWNLOAD_LOCUS_PLAN_CSV_FAIL,
  error,
});

export const downloadPlanOutputCsvSuccess = () => ({
  type: actionTypes.DOWNLOAD_PLAN_OUTPUT_CSV_SUCCESS,
});

export const getRiderJobTypesInit = () => ({
  type: actionTypes.GET_RIDER_JOB_TYPES_INIT,
});

export const getRiderJobTypesSuccess = (payload: IJobTypes | null) => ({
  type: actionTypes.GET_RIDER_JOB_TYPES_SUCCESS,
  payload,
});

export const getRiderJobTypesFail = (error: string | object | null) => ({
  type: actionTypes.GET_RIDER_JOB_TYPES_FAIL,
  error,
});

export const createRiderUser =
  (payload: ICreateUpdateRiderRequestPayload) =>
  (dispatch: Dispatch<IActionType>) => {
    dispatch(createRiderManagementUserInit());
    const createRiderUserPayload: any = {
      name: payload.fullName,
      phone: payload.phoneNumber.trim(),
      password: payload.password,
      passwordConfirmation: payload.confirmPassword,
      nationalId: payload.nationalId,
      email: payload.email,
    };
    return userSource
      .createUser(
        UserAddUpdateDto.fromRiderCreateDto(null, createRiderUserPayload)
      )
      .then((response) => {
        const riderPayload: any = {
          userId: response.id,
          fullName: payload.fullName,
          phoneNumber: payload.phoneNumber.trim(),
          title: appendSubTypeWithJobTypeForRiderTitle(
            payload.subType,
            payload.jobType
          ),
          status: payload.status,
          jobType: payload.jobType,
          subType: payload.subType,
          warehouseId: payload.warehouseId,
        };
        return createRiderApiCall(riderPayload);
      })
      .then(() => dispatch(createRiderManagementUserSuccess()))
      .catch((e: object | null | string) =>
        dispatch(createRiderManagementUserFail(e))
      );
  };

export const getRiderManagementList =
  (queryParams?: IGetQueryParams) => (dispatch: Dispatch<IActionType>) => {
    dispatch(getRiderManagementListInit());
    return fetchRiderListApiCall(queryParams)
      .then((riders: any) => dispatch(getRiderManagementListSuccess(riders)))
      .catch((e: object | null | string) => {
        dispatch(getRiderManagementListFail(e));
      });
  };

export const getRiderUserDetailsAction =
  (id: string) => (dispatch: Dispatch<IActionType>) => {
    dispatch(getRiderUserDetailsInit());
    return getRiderUserDetailsApiCall(id)
      .then((userDetails: IUserDetailsResponseObject) =>
        dispatch(getRiderUserDetailsSuccess(userDetails))
      )
      .catch((e: object | null | string) =>
        dispatch(getRiderUserDetailsFail(e))
      );
  };

export const createRider =
  (payload: ICreateRiderRequestPayload) =>
  (dispatch: Dispatch<IActionType>) => {
    dispatch(createRiderInit());
    return createRiderApiCall(payload)
      .then(() => dispatch(createRiderSuccess()))
      .catch((e: object | null | string) => dispatch(createRiderFail(e)));
  };

export const updateRiderUser =
  (payload: ICreateUpdateRiderManagementUserNormalizedReq) =>
  (dispatch: Dispatch<IActionType>) => {
    dispatch(updateRiderManagementUserInit());
    const updateUserPayload: any = {
      name: payload.fullName,
      phone: payload.phoneNumber.trim(),
      nationalId: payload.nationalId,
      password: payload.password,
      passwordConfirmation: payload.confirmPassword,
      status: payload.status,
      email: payload.email,
    };

    const riderPayload: IRiderManagementNormalized = {
      fullName: payload.fullName,
      userId: payload.userId,
      title: payload.title,
      phoneNumber: payload.phoneNumber.trim(),
      status: payload.status,
      jobType: payload.jobType,
      subType: payload.subType,
      warehouseId: payload.warehouseId,
    };

    return userSource
      .updateUser(
        payload.userId as string,
        UserAddUpdateDto.fromUpdateDto(
          payload.userId as string,
          updateUserPayload
        )
      )
      .then(() => {
        return updateRiderApiCall(riderPayload);
      })
      .then(() => dispatch(updateRiderManagementUserSuccess()))
      .catch((e: object | null | string) =>
        dispatch(updateRiderManagementUserFail(e))
      );
  };

export const downloadLocusCsvAction =
  (payload: IPlanCreation) => (dispatch: Dispatch<IActionType>) => {
    dispatch(downloadLocusCsvInit());
    return getLocusCsvApiCall(payload)
      .then((locusFile: any) => dispatch(downloadLocusCsvSuccess(locusFile)))
      .catch((e: object | null | string) => dispatch(downloadLocusCsvFail(e)));
  };

export const downloadPlanOutputCsvAction =
  (planId: string) => (dispatch: Dispatch<IActionType>) => {
    dispatch(downloadLocusCsvInit());
    return getPlanOutputCsvApiCall(planId)
      .then(() => dispatch(downloadPlanOutputCsvSuccess()))
      .catch((e: object | null | string) => dispatch(downloadLocusCsvFail(e)));
  };

export const getRiderJobTypesAction =
  () => (dispatch: Dispatch<IActionType>) => {
    dispatch(getRiderJobTypesInit());
    return getRiderJobTypeApiCall()
      .then((data) => dispatch(getRiderJobTypesSuccess(data)))
      .catch((e: object | null | string) => dispatch(getRiderJobTypesFail(e)));
  };
